import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { AuthRequired } from "../components/AuthRequired";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Button, Disclosure } from "../ui/primitives";
import {
  ErrorBlock,
  LoadingBlock,
  MetricCard,
  MoneyValue,
  StatusChip,
} from "../components/CrmCards";
import { blockingReasonLabel, displayPayableStatus, nextActionLabel } from "../lib/payableLabels";
import { mapAffiliateError } from "../api/affiliatePayoutsErrors";
import {
  approvePartnerPayable,
  cancelPartnerPayable,
  getPartnerPayable,
  getTreasurySummary,
  recordPartnerPayableTx,
  type PartnerPayableDetailResponse,
  type TreasurySummaryResponse,
} from "../api/treasuryPayables";

const OPEN_REQUEST_STATUSES = new Set(["requested", "approved", "awaiting_tx", "submitted"]);

// Owner-gated "Cancel request" — only for an OPEN request (never paid). Reason
// required + typed confirmation. Sends NO transaction; flips the request to
// cancelled via the BE cancel endpoint, then refetches.
function CancelRequestPanel({ targetId, onDone }: { targetId: string; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function doCancel() {
    setBusy(true);
    setError(null);
    try {
      await cancelPartnerPayable(targetId, reason.trim());
      setOpen(false);
      setReason("");
      onDone();
    } catch (e) {
      setError(mapAffiliateError(e).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Disclosure summary="Danger zone — cancel this payout request" tone="danger">
      <div
        style={{
          fontSize: 12.5,
          color: "var(--fg-dim)",
          lineHeight: 1.5,
          margin: "10px 0 8px",
        }}
      >
        Cancel this open payout request (e.g. wrong/stale, or a bad destination). This sends
        <strong> no transaction</strong> — it only closes the request so it stops counting as open.
      </div>
      <label htmlFor="cancel-reason" style={{ display: "block", fontSize: 12, color: "var(--fg-dim)", marginBottom: 4 }}>
        Reason (required)
      </label>
      <input
        id="cancel-reason"
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. duplicate / wrong destination"
        style={{ width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid var(--border)" }}
      />
      <Button
        variant="danger"
        disabled={reason.trim().length === 0}
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
        style={{ marginTop: 10 }}
      >
        Cancel request
      </Button>
      <ConfirmDialog
        open={open}
        title="Cancel payout request"
        tone="danger"
        description="This closes the open payout request. No transaction is sent."
        rows={[
          { key: "Payout id", value: targetId },
          { key: "Reason", value: reason.trim() },
        ]}
        requireTypedConfirmation={{ expected: "CANCEL", label: 'Type "CANCEL" to confirm' }}
        confirmLabel="Confirm cancel"
        busy={busy}
        error={error}
        onConfirm={() => void doCancel()}
        onCancel={() => setOpen(false)}
      />
    </Disclosure>
  );
}

// Treasury Ledger Payout v1 — partner-payable detail + owner-gated CRM
// actions (Approve, Record txHash). The browser NEVER broadcasts/signs a
// transaction: Approve marks a liability approved; Record txHash POSTs a hash
// of a transfer the operator already sent, which the BE verifies on-chain
// read-only. No proposalId / invoiceId / escrow bucket anywhere.

interface DetailData {
  payable: PartnerPayableDetailResponse;
  summary: TreasurySummaryResponse | null;
}

const TXHASH_RE = /^0x[0-9a-fA-F]{64}$/;

function parseHuman(human: string): number {
  const n = Number(human.replace(/,/g, ""));
  return Number.isFinite(n) ? n : NaN;
}

function SolvencyCard({ data }: { data: DetailData }) {
  const available = data.payable.payableSummary.available;
  const bal = data.summary?.treasuryBalance;
  if (!bal || !bal.available) {
    return (
      <MetricCard title="Treasury solvency" accent="var(--fg-dim, #888)" footer="Indicative only — not a payout gate.">
        <div style={{ fontSize: 13, color: "var(--fg-dim)" }}>
          Treasury balance unavailable — cannot show coverage.
        </div>
      </MetricCard>
    );
  }
  const need = parseHuman(available.human);
  const have = parseHuman(bal.human);
  const covered = Number.isFinite(need) && Number.isFinite(have) && have >= need;
  return (
    <MetricCard
      title="Treasury solvency"
      accent={covered ? "var(--ok)" : "var(--warn)"}
      badge={<StatusChip status={covered ? "ready" : "pending"} />}
      footer="Indicative comparison of treasury balance vs this payable. Not a payout gate; the BE re-checks solvency at approve time."
    >
      <div style={{ fontSize: 13, lineHeight: 1.6 }}>
        Treasury holds <strong>{bal.displayAmount}</strong>
        <br />
        This payable needs <strong>{available.displayAmount}</strong>
        <div style={{ marginTop: 6, color: covered ? "var(--ok)" : "var(--warn)", fontWeight: 600 }}>
          {covered ? "Appears covered" : "May be under-funded — review"}
        </div>
      </div>
    </MetricCard>
  );
}

// ── Owner-gated action panel (status-driven) ────────────────────────

function ActionPanel({
  detail,
  targetId,
  onDone,
}: {
  detail: PartnerPayableDetailResponse;
  targetId: string;
  onDone: () => void;
}) {
  const [modal, setModal] = useState<null | "approve" | "record">(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState("");

  const req = detail.request;
  const reqStatus = req?.status ?? null;
  const amount = req?.amount ?? detail.payableSummary.available;
  const destination = detail.destination;
  const txValid = TXHASH_RE.test(txHash.trim());

  async function doApprove() {
    setBusy(true);
    setError(null);
    try {
      await approvePartnerPayable(targetId);
      setModal(null);
      onDone();
    } catch (e) {
      setError(mapAffiliateError(e).message);
    } finally {
      setBusy(false);
    }
  }

  async function doRecord() {
    setBusy(true);
    setError(null);
    try {
      await recordPartnerPayableTx(targetId, txHash.trim());
      setModal(null);
      setTxHash("");
      onDone();
    } catch (e) {
      setError(mapAffiliateError(e).message);
    } finally {
      setBusy(false);
    }
  }

  // BLOCKED — an open request whose destination is missing/invalid can never
  // be paid. Show a clear block, never an Approve / Record txHash action, and
  // never a "send to —" instruction. (BE marks these status=blocked +
  // nextAction=fix_destination_or_cancel.)
  const destValid = !!destination && /^0x[0-9a-fA-F]{40}$/.test(destination);
  const isOpen =
    reqStatus === "requested" || reqStatus === "approved" || reqStatus === "awaiting_tx" || reqStatus === "submitted";
  if (detail.nextAction === "fix_destination_or_cancel" || (isOpen && !destValid)) {
    return (
      <MetricCard title="Blocked — missing destination" accent="var(--danger)" badge={<StatusChip status="blocked" />}>
        <div style={{ fontSize: 13, lineHeight: 1.55, color: "var(--danger)", fontWeight: 600 }}>
          Missing destination wallet — cannot pay.
        </div>
        <div style={{ fontSize: 12, color: "var(--fg-dim)", marginTop: 6, lineHeight: 1.5 }}>
          This payout request has no valid payout wallet, so it cannot be approved or paid.
          The partner must re-request with a valid wallet, or an operator can cancel it.
        </div>
        {detail.onFilePayoutAddress && (
          <div style={{ fontSize: 12, marginTop: 8 }}>
            Saved payout wallet (on file):{" "}
            <code style={{ fontSize: 11, wordBreak: "break-all" }}>{detail.onFilePayoutAddress}</code>
          </div>
        )}
      </MetricCard>
    );
  }

  // requested → Approve
  if (reqStatus === "requested") {
    return (
      <MetricCard title="Action — approve payout" accent="var(--ok)" badge={<StatusChip status="pending" />}>
        <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 10 }}>
          Approve this payout request. This marks the liability approved and unlocks
          recording the treasury transfer. <strong>No funds move and no transaction is
          sent</strong> by this step.
        </div>
        {/* The page's one filled button. It was a hand-rolled solid #0a9444
            with white text — a colour outside the token system, so it could
            neither follow a token repoint nor carry the scoped light palette,
            and it competed with the equally-solid red Cancel button below it
            for "brightest thing on the screen". Approve is the primary action
            here, so it takes the primary variant; destructive stays outlined. */}
        <Button
          variant="primary"
          onClick={() => {
            setError(null);
            setModal("approve");
          }}
        >
          Approve payout
        </Button>
        <ConfirmDialog
          open={modal === "approve"}
          title="Approve payout"
          description="Confirm the amount and destination. This approves a liability — it sends no transaction."
          rows={[
            { key: "Amount", value: <strong>{amount.displayAmount}</strong> },
            { key: "Raw", value: amount.rawAmount },
            { key: "Destination", value: destination ?? "—" },
            { key: "Payout id", value: req?.payoutId ?? targetId },
          ]}
          requireTypedConfirmation={{
            expected: amount.human,
            label: `Type the amount "${amount.human}" to confirm`,
          }}
          confirmLabel="Confirm approval"
          busy={busy}
          error={error}
          onConfirm={() => void doApprove()}
          onCancel={() => setModal(null)}
        />
      </MetricCard>
    );
  }

  // approved | awaiting_tx → Record txHash
  if (reqStatus === "approved" || reqStatus === "awaiting_tx") {
    return (
      <MetricCard title="Action — record treasury transfer" accent="var(--accent, #2c7be5)" badge={<StatusChip status="ready" />}>
        <div style={{ fontSize: 13, lineHeight: 1.55 }}>
          <strong>Step 1 (off-band):</strong> from the Gao Treasury wallet, send exactly{" "}
          <strong>{amount.displayAmount}</strong> of USDC to:
          <div style={{ fontFamily: "monospace", fontSize: 12, margin: "6px 0", wordBreak: "break-all" }}>
            {destination ?? "—"}
          </div>
          <strong>Step 2:</strong> paste the transaction hash below. The backend verifies it
          on-chain — this page <strong>never sends a transaction</strong>.
        </div>
        <div style={{ marginTop: 10 }}>
          <label htmlFor="txhash" style={{ display: "block", fontSize: 12, color: "var(--fg-dim)", marginBottom: 4 }}>
            Transaction hash (0x + 64 hex)
          </label>
          <input
            id="txhash"
            type="text"
            value={txHash}
            onChange={(e) => setTxHash(e.target.value)}
            placeholder="0x…"
            autoComplete="off"
            spellCheck={false}
            style={{
              width: "100%",
              padding: "6px 8px",
              fontFamily: "monospace",
              fontSize: 13,
              border: txHash === "" ? "1px solid var(--border)" : txValid ? "1px solid var(--ok, #0a9444)" : "1px solid var(--danger, #d23f3f)",
            }}
          />
          {txHash !== "" && !txValid && (
            <div style={{ fontSize: 11, color: "var(--danger, #d23f3f)", marginTop: 4 }}>
              Must be 0x followed by 64 hex characters.
            </div>
          )}
        </div>
        {/* White-on-amber failed contrast once --accent stopped being the
            blue the #2c7be5 fallback implied. The primary variant pairs the
            accent ground with --accent-fg, which is solved against it. */}
        <Button
          variant="primary"
          disabled={!txValid}
          onClick={() => {
            setError(null);
            setModal("record");
          }}
          style={{ marginTop: 10 }}
        >
          Record txHash
        </Button>
        <ConfirmDialog
          open={modal === "record"}
          title="Record treasury transfer"
          description="Confirm the txHash of the transfer you already sent. The backend verifies it on-chain (read-only). No transaction is sent from here."
          rows={[
            { key: "txHash", value: txHash.trim() },
            { key: "Amount", value: <strong>{amount.displayAmount}</strong> },
            { key: "Destination", value: destination ?? "—" },
          ]}
          confirmLabel="Confirm record"
          busy={busy}
          error={error}
          onConfirm={() => void doRecord()}
          onCancel={() => setModal(null)}
        />
      </MetricCard>
    );
  }

  // submitted → pending confirmation
  if (reqStatus === "submitted") {
    return (
      <MetricCard title="Transfer submitted" accent="var(--warn)" badge={<StatusChip status="pending" />} footer="The backend re-verifies confirmations; refresh to update.">
        <div style={{ fontSize: 13, lineHeight: 1.5 }}>
          Transfer submitted — <strong>pending confirmation</strong>.
        </div>
        {detail.execution?.txHash && (
          <div style={{ fontSize: 12, marginTop: 8 }}>
            txHash:{" "}
            <code style={{ fontSize: 11 }} title={detail.execution.txHash}>
              {detail.execution.txHashShort ?? detail.execution.txHash}
            </code>
            <div style={{ color: "var(--fg-dim)", marginTop: 2 }}>
              execution: {detail.execution.status}
            </div>
          </div>
        )}
      </MetricCard>
    );
  }

  // paid → done
  if (reqStatus === "paid" || detail.status === "paid") {
    return (
      <MetricCard title="Paid" accent="var(--ok)" badge={<StatusChip status="paid" />}>
        <MoneyValue display={amount.displayAmount} raw={amount.rawAmount} size={20} tone="var(--ok)" />
        {detail.execution?.txHash && (
          <div style={{ fontSize: 12, marginTop: 8 }}>
            txHash:{" "}
            <code style={{ fontSize: 11 }} title={detail.execution.txHash}>
              {detail.execution.txHashShort ?? detail.execution.txHash}
            </code>
            {detail.execution.verifiedAt && (
              <div style={{ color: "var(--fg-dim)", marginTop: 2 }}>
                verified: <code style={{ fontSize: 11 }}>{detail.execution.verifiedAt}</code>
              </div>
            )}
          </div>
        )}
      </MetricCard>
    );
  }

  // failed → review notice
  if (reqStatus === "failed") {
    return (
      <MetricCard
        title="Review failed payout"
        accent="var(--danger)"
        badge={<StatusChip status="blocked" />}
        footer={detail.blockingReason ? `Blocked: ${blockingReasonLabel(detail.blockingReason)}` : undefined}
      >
        <div style={{ fontSize: 13, color: "var(--danger)", lineHeight: 1.5 }}>
          The last recorded transfer failed verification — review and re-record once a valid transfer is sent.
        </div>
      </MetricCard>
    );
  }

  // No actionable request: a payable balance with no open request (or the
  // latest request is closed). Make it explicit that the partner must request
  // a withdrawal before the admin can approve.
  const noOpenRequest = req === null || detail.nextAction === "awaiting_request";
  return (
    <MetricCard
      title="No payout request yet"
      accent="var(--accent, #2c7be5)"
      badge={
        detail.status === "ready" && detail.nextAction === "awaiting_request" ? (
          <StatusChip status="available" />
        ) : undefined
      }
      footer={detail.blockingReason ? `Blocked: ${blockingReasonLabel(detail.blockingReason)}` : undefined}
    >
      <div style={{ fontSize: 13, color: "var(--fg)", lineHeight: 1.55 }}>
        {noOpenRequest
          ? "No payout request yet. Partner must request withdrawal before admin can approve."
          : "Nothing to act on — the latest request is closed."}
        <div style={{ fontSize: 12, color: "var(--fg-dim)", marginTop: 6 }}>
          Available balance: <strong>{detail.payableSummary.available.displayAmount}</strong>
        </div>
      </div>
    </MetricCard>
  );
}

function PartnerPayableDetailInner({ id }: { id: string }) {
  const [data, setData] = useState<DetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const payable = await getPartnerPayable(id);
      let summary: TreasurySummaryResponse | null = null;
      try {
        summary = await getTreasurySummary();
      } catch {
        summary = null;
      }
      setData({ payable, summary });
    } catch (e) {
      setError(mapAffiliateError(e).message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && data === null) return <LoadingBlock label="Loading payable…" />;
  if (error) return <ErrorBlock message={error} onRetry={() => void load()} />;
  if (data === null) return <LoadingBlock label="Loading payable…" />;

  const p = data.payable;
  const s = p.payableSummary;
  // Mutation target: prefer the explicit payout_request id; fall back to the
  // route id (the BE resolves either a request id or an identity id).
  const targetId = p.request?.payoutId ?? id;

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", margin: "4px 0 14px 0" }}>
        <Link to="/admin/partner-payables" style={{ fontSize: 12.5, color: "var(--accent, #2c7be5)", textDecoration: "none" }}>
          ← Back to payables
        </Link>
        <span style={{ marginLeft: "auto" }}>
          <StatusChip status={displayPayableStatus({ status: p.status, nextAction: p.nextAction })} />
        </span>
        <button type="button" onClick={() => void load()} disabled={loading} style={{ padding: "4px 12px", fontSize: 13 }}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Owner-gated action panel — the primary operator surface. */}
      <div style={{ marginBottom: 12 }}>
        <ActionPanel detail={p} targetId={targetId} onDone={() => void load()} />
      </div>

      {/* Cancel — only for an OPEN request (never paid). */}
      {p.request && OPEN_REQUEST_STATUSES.has(p.request.status) && (
        <div style={{ marginBottom: 12 }}>
          <CancelRequestPanel targetId={targetId} onDone={() => void load()} />
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          // Without this, every card in a row is stretched to the height of
          // the tallest one — so the four-row Payable summary left Partner and
          // Destination as mostly-empty boxes, which reads as missing data.
          alignItems: "start",
          gap: 12,
        }}
      >
        <MetricCard title="Partner" footer={p.partner.partnerApproved ? "Approved partner." : "Not an approved partner."}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{p.partner.refCode ?? "—"}</div>
          <div style={{ fontFamily: "monospace", fontSize: 11, color: "var(--fg-dim)", marginTop: 4 }}>
            {p.identityId}
          </div>
        </MetricCard>

        <MetricCard title="Destination" footer="Active payout request destination — where the treasury transfer is sent.">
          <code style={{ fontSize: 13, wordBreak: "break-all" }} title={p.destination ?? undefined}>
            {p.destination ?? "—"}
          </code>
          {p.onFilePayoutAddress && p.onFilePayoutAddress !== p.destination && (
            <div style={{ fontSize: 11, color: "var(--fg-dim)", marginTop: 8 }}>
              Saved payout wallet (on file):{" "}
              <code style={{ fontSize: 11, wordBreak: "break-all" }}>{p.onFilePayoutAddress}</code>
            </div>
          )}
        </MetricCard>

        <MetricCard title="Available to pay" accent="var(--ok)">
          <MoneyValue display={s.available.displayAmount} raw={s.available.rawAmount} />
        </MetricCard>

        <MetricCard title="Payable summary" accent="var(--accent, #2c7be5)" footer="available = earned − paid − open (BE-computed).">
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <tbody>
              {([
                ["Earned", s.earned],
                ["Paid", s.paid],
                ["Open request", s.openRequest],
                ["Available", s.available],
              ] as const).map(([label, m]) => (
                <tr key={label}>
                  <td style={{ padding: "3px 0", color: "var(--fg-dim)" }}>{label}</td>
                  <td style={{ padding: "3px 0", textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                    {m.displayAmount}
                  </td>
                  <td style={{ padding: "3px 0 3px 10px", textAlign: "right", fontSize: 10, color: "var(--fg-dim)" }}>
                    {m.rawAmount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </MetricCard>

        <SolvencyCard data={data} />

        <MetricCard title="Risk checks" footer="Read-only preconditions the BE evaluates before any payout.">
          <ul style={{ margin: 0, padding: 0, listStyle: "none", fontSize: 13 }}>
            {p.risk.map((r) => (
              <li key={r.check} style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 0" }}>
                <span style={{ color: r.ok ? "var(--ok)" : "var(--danger)", fontWeight: 700 }}>{r.ok ? "✓" : "✗"}</span>
                <span>{r.check.replace(/_/g, " ")}</span>
              </li>
            ))}
          </ul>
        </MetricCard>

        <MetricCard
          title="Next action"
          accent="var(--warn)"
          footer={p.blockingReason ? `Blocked: ${blockingReasonLabel(p.blockingReason)}` : "Driven by the action panel above."}
        >
          <div style={{ fontSize: 16, fontWeight: 700 }}>{nextActionLabel(p.nextAction)}</div>
          {p.request && (
            <div style={{ fontSize: 12, color: "var(--fg-dim)", marginTop: 8 }}>
              Latest request <code style={{ fontSize: 11 }}>{p.request.payoutId}</code> —{" "}
              {p.request.status} · {p.request.amount.displayAmount}
              {p.request.rejectionReason && (
                <div style={{ color: "var(--danger)", marginTop: 2 }}>reason: {p.request.rejectionReason}</div>
              )}
            </div>
          )}
        </MetricCard>
      </div>
    </div>
  );
}

export function PartnerPayableDetail() {
  const { id } = useParams<{ id: string }>();
  return (
    <div>
      <PageHeader
        title="Partner payable"
        subtitle="Payable detail + owner-gated actions. Approve marks a liability; Record txHash logs a transfer you already sent — no transaction is sent from here."
        method="GET"
        path="/v2/admin/bff/partner-payables/:id"
      />
      <AuthRequired>
        {id ? <PartnerPayableDetailInner id={id} /> : <ErrorBlock message="Missing payable id in the URL." />}
      </AuthRequired>
    </div>
  );
}
